import { LogInstanceToken } from '@evorait/pace-logging';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { UriUtilitiesService } from '../../services/uri-utilities/uri-utilities.service';
import { Notification } from '../../types/notification';
import { NotificationIdentifier } from '../../types/notification-identifier';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { TextInputUtilities } from '../../utilities/text-input-utilities';
import { TimeUtilities } from '../../utilities/time-utilities';
import { AddressModel, ODataShortAddressModel } from '../complex/address.model';
import { CodeModel, ODataCodeModel } from '../complex/code.model';
import { ContractModel, ODataContractModel } from '../complex/contract.model';
import { DopModel, ODataDopModel } from '../complex/dop.model';
import { EmailModel, ODataEmailModel } from '../complex/email.model';
import { EquipmentModel, ODataEquipmentModel } from '../complex/equipment.model';
import { HistoryModel, ODataHistoryModel } from '../complex/history.model';
import { InternalInfoModel } from '../complex/internal-info/internal-info.model';
import { ODataPartModel, PartModel } from '../complex/part.model';
import { ODataParticipantModel, ParticipantModel } from '../complex/participant.model';
import { ODataTaskModel, TaskModel } from '../complex/task.model';
import { TimeZoneModel } from '../complex/time-zone.model';
import { NotificationIdentifierModel } from './notification-identifier.model';
import { StatusModel } from './status.model';
import { centriCareContract, contractTypeLabel, sourceReplacementCodeInLText } from './notification.constants';
import { SourceReplacementModel } from '../complex/source-replacement.model';
import { RadioActiveModel } from './radio-active.model';

export class NotificationModel {
    protected clientManager: ClientManagerInterface;

    // P58 model structure
    public readonly functionalLoc: string;
    public readonly mType: string;
    public readonly identifier: NotificationIdentifierModel;
    public readonly companyCode: string;
    public readonly backendStatus: StatusModel; // "Status" : "DISP"
    public readonly vorNr: number; // "Vornr" : "0010"
    public readonly orderType: string; // "Auart" : "MZ01",
    public readonly plannedStart: moment.Moment;
    public readonly plannedEnd: moment.Moment;
    public readonly plannedStartDate: string;
    public readonly plannedStartTime: string;
    public readonly plannedEndDate: string;
    public readonly plannedEndTime: string;
    public readonly timeZone: TimeZoneModel;
    public readonly address: AddressModel;
    public readonly phoneRemote: string; // "TelRemote" : "",
    public readonly customerPurchaseOrderNr: string; // "CusPurOrderNr" : "",
    public readonly clarifyText1: string; // "Caller" : "FELIX LOHIDAJAT/123456789 /EXT",
    public readonly dispatcher: string; // "Dispatcher" : "Zhi Haolin",
    public readonly preclarified: number; // "PreClarified" : "0",
    public readonly clarifyText: string; // "ClarifyText" : "",
    public readonly notificationText: string; // "NotificationText": "JACOB MCCANDLES/123456789 /EXT",
    public readonly repairLimit: number; // "RepairLimit" : "000000",
    public readonly assigned: string; // "Assigned" : "",
    public readonly ndpVersion: number; // "NdpVersion" : "00000",
    public readonly anlInaktCode: string; // "AnlInaktCode" : "",
    public readonly effect: string; // "Effect" : "3",
    public readonly startMalfunction: moment.Moment; // "StartMalfunctionDate" : null, "StartMalfunctionTime" : "PT00H00M00S",
    public readonly notificationType: ConfigConstants.NotificationTypes; // "Qmart" : "MI",
    public readonly processIndicator: ConfigConstants.PROCESS_INDICATOR; // "InvitroInd" : "",
    public readonly userStatus: ConfigConstants.USER_STATUS; // "UserStatus" : "",
    public readonly mqQmnum: string; // "MqQmnum" : "",
    public readonly taskGroup: string; // "TaskGroup" : "CSE",
    public readonly isApplicationSpecialist: boolean; // convenience function for easier interpretation of taskGroup
    public readonly languageId: string; // "Language" : "", default value 'EN'
    public readonly languageText: string; // "Language" : "",
    public readonly formsCount: number;
    public readonly hotSiteLongtext: string;
    public readonly salesOrg: string;
    public readonly notificationLongtext: ODataLTextModel[];
    public readonly notificationShippingPriorities: Array<{
        code: string;
        type: string;
    }>;
    public readonly sourceReplacements: SourceReplacementModel[];
    public readonly troubleLongtext: string;
    public readonly objectInformationLongInfo: ODataLTextModel[];
    public readonly objectInformationLongText: string;
    public readonly preclarificationLongtext: ODataLTextModel[];
    public readonly historyLongInfo: ODataLTextModel[];
    public readonly partnerEmailAddresses: string[];
    public readonly deliveryType: string;
    public readonly equipmentsCount: number;

    public readonly addressSet: AddressModel[];
    public readonly codeSet: CodeModel[];
    public readonly contract: ContractModel;
    public readonly dopSet: DopModel[];
    public readonly emailSet: EmailModel[];
    public readonly equipmentSet: EquipmentModel[];
    public readonly historySet: HistoryModel[];
    public readonly partSet: PartModel[];
    public readonly participantSet: ParticipantModel[];
    public readonly taskSet: TaskModel[];
    public readonly radioActiveSurveyModel: RadioActiveModel[] | undefined;
    public readonly isPaceFormsEnabled: boolean;
    public readonly internalInfoModel: InternalInfoModel;
    public readonly taxiLongInfo: ODataLTextModel[];

    // in-memory properties (Need to be set initially at app-start (notification-service init()))
    public dispOrEtaStart: moment.Moment;
    public dispOrEtaEnd: moment.Moment;
    public updatedStatus: StatusModel;
    public isEscalated: boolean;
    public isCancelled: string;
    public isUnread: boolean;
    public isPaused: boolean;
    public csmlTrackerUrl: string;
    public rentedMaterials: ODataLTextModel[];
    public isRemoteSupport: boolean;

    private readonly uriUtilities: UriUtilitiesService;
    public constructor(notification: ODataNotificationModel) {
        const injector = CustomInjector.getInjector();
        const log = injector.get(LogInstanceToken);
        const configService = injector.get(ConfigService);
        const notificationUtilities = injector.get(NotificationUtilities);
        this.clientManager = injector.get(ClientManagerInstanceToken);
        const now = moment();
        this.uriUtilities = injector.get(UriUtilitiesService);

        this.companyCode = notification.CompanyCode;
        this.functionalLoc = notification.FunctionalLoc;
        this.mType = notification.MessagetypeCSE;
        this.identifier = new NotificationIdentifierModel(
            notification.FrontId,
            notification.NotificationNo,
            notification.TaskNumber,
        );

        this.backendStatus = notification.Status
            ? new StatusModel(<NOTIFICATION_STATUS_CODE>notification.Status, now, now)
            : new StatusModel(NOTIFICATION_STATUS_CODE.DISP, now, now); // TODO: Check if it is a valid case to receive "" here

        this.vorNr = notification.ActivityNo ? parseInt(notification.ActivityNo, 10) : NaN;
        this.orderType = notification.SalesDocType;
        const formatMomentInUtc = configService.getFormatMomentInUTC();
        this.plannedStartDate = notification.PlanStartDate;
        this.plannedStartTime = notification.PlanStartTime;
        this.plannedEndDate = notification.PlanEndDate;
        this.plannedEndTime = notification.PlanEndTime;
        this.plannedStart = TimeUtilities.momentFromDateAndTime(
            notification.PlanStartDate,
            notification.PlanStartTime,
            formatMomentInUtc,
        );
        this.plannedEnd = TimeUtilities.momentFromDateAndTime(
            notification.PlanEndDate,
            notification.PlanEndTime,
            formatMomentInUtc,
        );
        this.timeZone = new TimeZoneModel(
            moment.duration(notification.UTCDiff),
            notification.UTCSign,
            notification.TimeZone,
        );

        this.address = AddressModel.fromOData(notification);
        this.phoneRemote = notification.TelRemote;
        this.customerPurchaseOrderNr = notification.CustPONo ? notification.CustPONo : 'n/a';
        this.clarifyText1 = notification.ClarifyText1;
        this.dispatcher = notification.Dispatcher;
        this.preclarified = notification.PreClarified ? parseInt(notification.PreClarified, 10) : NaN;
        this.clarifyText = notification.ClarifyText;
        this.notificationText = notification.NotificationText;
        this.repairLimit = notification.RepairLimit ? parseInt(notification.RepairLimit, 10) : NaN;
        this.assigned = notification.Assigned;
        this.ndpVersion = notification.NdpVersion ? parseInt(notification.NdpVersion, 10) : NaN;
        this.anlInaktCode = notification.AnlInaktCode;
        this.effect = notification.Effect;
        this.startMalfunction = TimeUtilities.momentFromDateAndTime(
            notification.StartMalfunctionDate,
            notification.StartMalfunctionTime,
        );
        this.notificationType = notification.NotificationType;
        if (
            notification.InvitroInd === ConfigConstants.PROCESS_INDICATOR.INVIVO ||
            notification.InvitroInd === ConfigConstants.PROCESS_INDICATOR.INVITRO
        ) {
            this.processIndicator = <ConfigConstants.PROCESS_INDICATOR>notification.InvitroInd;
        } else {
            this.processIndicator = ConfigConstants.PROCESS_INDICATOR.UNKNOWN;
        }
        this.userStatus = notification.UserStatus;
        this.isRemoteSupport =
            configService.getEnableRemoteWork() && notification.UserStatus.includes(ConfigConstants.USER_STATUS.REMT);
        this.mqQmnum = notification.Notification;
        this.taskGroup = notification.TaskGroup;
        this.isApplicationSpecialist = this.taskGroup === configService.getAppsTaskCode();
        this.languageId = notification.LanguageId.length > 0 ? notification.LanguageId : 'EN';
        this.languageText = notification.LanguageText;
        this.formsCount = notification.FormsCount;
        this.salesOrg = notification.SalesOrg;

        const addressSet = notification.AddressSet.results || [];
        this.addressSet = addressSet.map((addr) => AddressModel.fromODataShort(addr));
        this.addressSet = _.sortBy(this.addressSet, 'pos.value');

        if (notification.CodeSet) {
            const codeSet = notification.CodeSet.results || [];
            this.codeSet = codeSet.map((code) => CodeModel.fromOData(code));
            this.codeSet = _.sortBy(this.codeSet, 'pos.value');
        } else {
            this.codeSet = [];
        }

        this.contract = ContractModel.fromOData(notification.ContractSet.results);

        const dopSet = notification.DopSet.results || [];
        this.dopSet = dopSet.map((dop) => DopModel.fromOData(dop));
        this.dopSet = _.sortBy(this.dopSet, 'pos.value');

        const emailSet = notification.EmailSet.results || [];
        this.emailSet = emailSet.map((email) => EmailModel.fromOData(email));
        this.emailSet = _.sortBy(this.emailSet, 'pos.value');

        const equipmentsSet = notification.EquipmentsSet.results || [];
        if (equipmentsSet.length === 0) {
            log.error(`NDPHeaderSet->EquipmentsSet is empty for notification ${this.identifier.toString()}.`);
            this.equipmentSet = [];
        } else {
            this.equipmentSet = equipmentsSet.map((eq) => EquipmentModel.fromOData(eq));
            this.equipmentSet = _.sortBy(this.equipmentSet, 'pos.value');
            if (this.equipmentSet.length === 0) {
                log.error(`equipmentSet is not created properly for notification ${this.identifier.toString()}.`);
            }
        }

        this.equipmentsCount = notification.EquipmentCount ?? equipmentsSet.length;

        const historySet = notification.HistorySet.results || [];
        this.historySet = historySet.map((hs) => HistoryModel.fromOData(hs));
        this.historySet = _.sortBy(this.historySet, 'pos.value');

        const partSet = notification.PartSet.results || [];
        this.partSet = partSet.map((part) => PartModel.fromOData(part));
        this.partSet = _.sortBy(this.partSet, 'pos.value');
        if (this.clientManager.isNgmP58()) {
            this.internalInfoModel = InternalInfoModel.buildInternalInfoModel(
                notification.IntInfLtextSet.results,
                this.identifier,
                this.functionalLoc,
            );
        }
        const participantSet = notification.ParticipantSet.results || [];
        this.participantSet = participantSet.map((participant) => ParticipantModel.fromOData(participant));
        this.participantSet = _.sortBy(this.participantSet, 'pos.value');

        const tasksSet = notification.TaskSet.results || [];
        this.taskSet = tasksSet.map((task) => TaskModel.fromOData(task));
        this.taskSet = _.sortBy(this.taskSet, 'pos.value');

        this.hotSiteLongtext = TextInputUtilities.getLongtext(notification.HotsiteLtextSet.results, '<br>', log);

        this.taxiLongInfo = notification.TaxiLtextSet?.results || [];

        // There might be other entries in the long text of the notification than the detail information, so we filter
        // for the entries referring to the notification long text before joining the content
        this.notificationLongtext = TextInputUtilities.getLongArray(
            notification.NotifLtextSet.results.filter((line) => line.TextID === 'QMEL'),
            log,
        );

        const effectCodeForShipping = 'ZPR' + this.effect;
        const shippingPriorities = notification.NotifLtextSet.results.find(
            (line) => line.TextID === effectCodeForShipping,
        );
        if (shippingPriorities) {
            this.notificationShippingPriorities = shippingPriorities.TextLine.split(',').map((priority) => {
                const [code, type] = priority.split('-');
                return { code, type };
            });
        }

        const sourceReplacements = notification.SorcLTextSet?.results?.filter(
            (line) => line.TextID === sourceReplacementCodeInLText,
        );
        if (sourceReplacements && sourceReplacements.length > 0) {
            // Only convert entries that do not contain a '?' in their TextLine
            const validSourceReplacements = sourceReplacements.filter(
                (line) => !(line.TextLine && line.TextLine.indexOf('?') >= 0),
            );
            this.sourceReplacements = validSourceReplacements.map((sourceReplacement) =>
                SourceReplacementModel.fromOData(sourceReplacement),
            );
        }

        // IssueRepLtextSet only exists in NGM AA
        this.troubleLongtext =
            notification.IssueRepLtextSet && configService.getPrefillTroubleText()
                ? TextInputUtilities.getLongtext(notification.IssueRepLtextSet.results, '\n', log)
                : '';
        this.objectInformationLongInfo = TextInputUtilities.getLongArray(notification.ObjInfLtextSet.results, log);
        this.objectInformationLongText = TextInputUtilities.getLongtext(
            notification.ObjInfLtextSet.results,
            '<br>',
            log,
        );
        this.preclarificationLongtext = TextInputUtilities.getLongArray(notification.PreClLtextSet.results, log);
        this.historyLongInfo = TextInputUtilities.getLongArray(notification.HistoryLtextSet.results, log);
        this.rentedMaterials = notification.RentLtextSet?.results || [];

        // as discussed, only e-mails with the `siemens` and `siemens-healthineers` are valid
        this.partnerEmailAddresses = this.getEmailsFromLongtextLinesArray(
            notification.NotifLtextSet.results.filter((line) => line.TextID === 'QMEL'),
            ['siemens', 'siemens-healthineers'],
        );
        const materialIdentifierList = this.partSet.map((partModel) => partModel.materialIdentifier);
        this.csmlTrackerUrl = this.uriUtilities.getCSMLTrackerURL(
            materialIdentifierList,
            configService.getCSMLTrackerLink(),
        );
        this.deliveryType = notification.DelivType;
        this.isPaceFormsEnabled = notificationUtilities.isPaceFormsAvailable(this.formsCount);

        // The radioactive tool is identified by the long text ID "ZFAD" – filter the Notification long text using ZFAD.
        // The TextLine for ZFAD follows the pattern <Functional-Location;Tool Name>, e.g., `004-100041;Gantry L/Chair Back`.
        // We need to remove the Functional Location part and display only the Tool Name.
        const filteredRadioActiveToolsList = notification.NotifLtextSet.results
            .filter((longText) => longText.TextLine.includes(';') && longText.TextID === 'ZFAD')
            .map((item) => item.TextLine.split(';')[1].trim());

        if (filteredRadioActiveToolsList.length > 0) {
            this.radioActiveSurveyModel = filteredRadioActiveToolsList.map((radioActiveTool) =>
                RadioActiveModel.fromOData(radioActiveTool),
            );
        }
    }

    public isMaterialAvailable(): boolean {
        return this.partSet.length > 0;
    }

    public isParticipantsAvailable(): boolean {
        return this.participantSet.length > 0;
    }

    public isCentriCareContract(): boolean {
        for (const section of this.contract.sections) {
            for (const entry of section.entries) {
                if (
                    entry.label === contractTypeLabel &&
                    entry.value.toLowerCase().replace(/\s+/g, '').includes(centriCareContract)
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    public getMainEquipment(): EquipmentModel | undefined {
        if (this.clientManager.isPaceUS()) {
            return this.equipmentSet[0];
        }

        return this.equipmentSet.find(
            (equipment: EquipmentModel) => equipment.mainEquipmentIndicator.value === ConfigConstants.sapTrue,
        );
    }

    // Following main notification data is required while fetching the other tasks of the notification by the GW
    public getNotificationRequestParamsForGetOtherTasks(): NotificationToGetOtherTasks {
        // extract only UTC date time stamp from the whole string as required by GW, "/Date(1505606400000)/" --> 1505606400000
        const startDate = this.plannedStartDate.replace(/\D/g, '');
        const endDate = this.plannedEndDate.replace(/\D/g, '');
        const statusCode = this.updatedStatus ? this.updatedStatus.statusCode : this.backendStatus.statusCode;

        const notifData: NotificationToGetOtherTasks = {
            DbNotifNo: this.identifier.qmNum,
            DbTaskNo: this.identifier.maNum,
            DbNotifStatus: statusCode,
            DbNotifStartDate: startDate,
            DbNotifStartTime: this.plannedStartTime,
            DbNotifEndDate: endDate,
            DbNotifEndTime: this.plannedEndTime,
        };
        return notifData;
    }

    public getAffectedEquipment(): EquipmentModel | undefined {
        if (this.clientManager.isNgmP58()) {
            return this.equipmentSet.find(
                (equipment: EquipmentModel) => equipment.mainEquipmentIndicator.value === 'S',
            );
        }
    }

    public getUniqueEquipmentsByEquipmentNumber(): EquipmentModel[] {
        return this.equipmentSet.filter(
            (equipment, index) =>
                this.equipmentSet.findIndex((equip) => equip.equnr.value === equipment.equnr.value) === index,
        );
    }

    public getEmailsFromLongtextLinesArray(lines: ODataLTextModel[], domains: string[]): string[] {
        const sortedLines = _.sortBy(lines, 'Counter');

        const text = sortedLines.map((line) => line.TextLine).join(' ');

        const regexDomains = domains.join('|');
        // regex to find all email addresses containing a specified domain
        // based on the link https://gist.github.com/seanbehan/be4d9dd7b31ca0d9ad12
        const regexString = `\\S+[a-z0-9]@(${regexDomains})(.com)+`;
        const regex = new RegExp(regexString, 'img');
        const matchingEmailsArray = text.match(regex);

        return matchingEmailsArray ? matchingEmailsArray : [];
    }

    /**
     * This method should be used only for PACE US.
     * In P58 and AA the separator used between name and number is `/
     * The separator used in PACE US is `:`
     */
    public getContactName(): string {
        let contactName = '';
        if (this.notificationText.length > 0) {
            // The contact name and number are available in the notificationText field, separated by ':'.
            // We only need to display the contact name
            const splittedNotificationText = this.notificationText.split(':');
            contactName = splittedNotificationText[0];
        }
        return contactName;
    }

    /**
     * This method should be used only for PACE US.
     * In P58 and AA the separator used between name and number is '/'
     * The separator used in PACE US is `:`
     */
    public getContactPhone(): string {
        let contactPhoneNumber = '';
        if (this.notificationText.length > 0) {
            // The contact name and number are available in the notificationText field, separated by ':'.
            // We only need to display the contact phone number
            const contactNameAndPhone = this.notificationText.split(':');
            contactPhoneNumber = contactNameAndPhone[1] ? contactNameAndPhone[1] : '';
        }
        return contactPhoneNumber;
    }

    public getNotificationState(): Notification {
        const identifier: NotificationIdentifier = {
            frontId: this.identifier.getFrontId(),
            qmNum: this.identifier.getQmnum(),
            maNum: this.identifier.getManum(),
        };

        const equipmentSet = this.equipmentSet.map((equipment) => equipment.getEquipmentState());

        return {
            identifier: identifier,
            backendStatus: this.backendStatus.getStatusState(),
            updatedStatus: this.updatedStatus
                ? this.updatedStatus.getStatusState()
                : this.backendStatus.getStatusState(),
            languageId: this.languageId,
            notificationType: this.notificationType,
            address: this.address.getAddressState(),
            equipmentSet: equipmentSet,
            emailSet: this.emailSet.map((email) => email.faxEmail.value),
            functionalLocation: this.functionalLoc,
        };
    }
}

export type NotificationToGetOtherTasks = {
    DbNotifNo: string;
    DbTaskNo: string;
    DbNotifStatus: string;
    DbNotifStartDate: string;
    DbNotifStartTime: string;
    DbNotifEndDate: string;
    DbNotifEndTime: string;
};

export type NotificationToModifyOtherTask = {
    FrontId: string;
    NotificationNo: string;
    TaskNo: string;
    TaskCodeGrp: string;
    TaskCode: string;
    TaskStatus: string;
    PlannedDate: string;
    PlannedEndDate: string;
    Deadline: string;
    PlannedStartTime: string;
    PlannedEndTime: string;
    EstimatedDuration: string;
    Equidescr: string;
    Equipment: string;
    ShortText: string;
};

export interface ODataLTextModel extends ODataObject {
    __metadata: {
        id: string;
        uri: string;
        type: string;
    };
    NotificationNo: string;
    TaskNumber: string;
    TextID: string;
    TextLine: string;
    Counter: number;
}

export interface ODataNotificationModel extends ODataObject {
    __metadata: {
        id: string;
        uri: string;
        type: 'HC_SM_NGM_58_SRV.NdpHeader';
    };
    FormsCount: number;
    EquipmentCount: number;
    Name1Long: string;
    NotificationNo: string;
    Name2Long: string;
    TaskNumber: string;
    FunctionalLoc: string;
    Name3Long: string;
    Name4Long: string;
    FrontId: string;
    MessagetypeCSE: string;
    CompanyCode: string;
    Plant: string;
    Division: string;
    SalesOrg: string;
    Status: string;
    ActivityNo: string;
    SalesDocType: string;
    PlanStartDate: string;
    PlanStartTime: string;
    PlanEndDate: string;
    PlanEndTime: string;
    UTCDiff: string;
    UTCSign: '+' | '-';
    TimeZone: string;
    Name1: string;
    Name2: string;
    Name3: string;
    Name4: string;
    Department: string;
    Room: string;
    Street: string;
    City: string;
    PostCode: string;
    Country: string;
    TelNumber: string;
    FaxNumber1: string;
    FaxNumber2: string;
    FaxNumber3: string;
    FaxNumber4: string;
    TelRemote: string;
    CustPONo: string;
    ClarifyText: string;
    NotificationText: string;
    ClarifyText1: string;
    Dispatcher: string;
    PreClarified: string;
    RepairLimit: string;
    Assigned: string;
    NdpVersion: string;
    ContactName1: string;
    ContactName2: string;
    ContactTel: string;
    ContactType: string;
    AnlInaktCode: string;
    Effect: string;
    StartMalfunctionDate: string;
    StartMalfunctionTime: string;
    NotificationType: ConfigConstants.NotificationTypes;
    Building: string;
    Floor: string;
    AddrNumber: string;
    InvitroInd: string;
    UserStatus: ConfigConstants.USER_STATUS;
    Notification: string;
    TaskGroup: string;
    LanguageId: string;
    LanguageText: string;
    DelivType: string;
    IntInfLtextSet: { results: ODataLTextModel[] };
    RentLtextSet: { results: ODataLTextModel[] };
    AddressSet: { results: ODataShortAddressModel[] };
    CodeSet: { results: ODataCodeModel[] };
    ContractSet: { results: ODataContractModel[] };
    DopSet: { results: ODataDopModel[] };
    EmailSet: { results: ODataEmailModel[] };
    EquipmentsSet: { results: ODataEquipmentModel[] };
    HistorySet: { results: ODataHistoryModel[] };
    HotsiteLtextSet: { results: ODataLTextModel[] };
    NotifLtextSet: { results: ODataLTextModel[] };
    SorcLTextSet: { results: ODataLTextModel[] };
    IssueRepLtextSet: { results: ODataLTextModel[] };
    ObjInfLtextSet: { results: ODataLTextModel[] };
    PartSet: { results: ODataPartModel[] };
    ParticipantSet: { results: ODataParticipantModel[] };
    PreClLtextSet: { results: ODataLTextModel[] };
    TaskSet: { results: ODataTaskModel[] };
    HistoryLtextSet: { results: ODataLTextModel[] };
    TaxiLtextSet: { results: ODataLTextModel[] };
}
